import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import { debounce } from 'lodash'

const MAX_PHOTO_SIZE_MB = 2; // Max size in megabytes

const VisitorFormVM = () => {
  // Update the handleToastOpen function in useToast provider to support custom durations
// You'll need to add this to the ToastProvider component or modify handleToastOpen to accept duration

const { handleToastOpen } = useToast();

// Modified handleToastOpen wrapper function to handle duration
const showToast = (type, message, duration) => {
  // If your existing handleToastOpen doesn't support duration, you can implement a workaround:
  // For example, if you're using react-toastify, you can directly access its API
  if (duration && window.toastify) {
    window.toastify[type](message, { autoClose: duration });
  } else {
    // Fall back to your regular handleToastOpen
    handleToastOpen(type, message);
  }
};
  const [isVerified, setIsVerified] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    contact: "",
    email: "",
    visitFrom: "",
    visitTill: "",
    accessTimeFrom: "09:00",    // ADDED TIME FIELD
    accessTimeTill: "17:00",    // ADDED TIME FIELD
    purpose: "",
    whomToVisitName: "",
    whomToVisitContact: "",
    whomToVisitEmail: "",
    photo: null,
    document: null,
    documentType: "",
  });

  // Add state to track which fields were verified successfully
  const [verifiedFields, setVerifiedFields] = useState({
    name: false,
    contact: false,
    email: false
  });

  // Add state to track if the Person to Visit section was auto-filled
  const [autoFilledPerson, setAutoFilledPerson] = useState(false);

  const [photoPreview, setPhotoPreview] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [openPhotoPreview, setOpenPhotoPreview] = useState(false);
  const [openDocumentPreview, setOpenDocumentPreview] = useState(false);
  const [isVerifyEnabled, setIsVerifyEnabled] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+91");
  const [selectedWhomToVisitCountryCode, setSelectedWhomToVisitCountryCode] =
    useState("+91");
  const [countryCodes, setCountryCodes] = useState([]);
  
  // Add state for visitor lookup
  const [isContactLookupEnabled, setIsContactLookupEnabled] = useState(true);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [previousVisitData, setPreviousVisitData] = useState(null);

  // Add state for the confirmation dialog
const [approverConfirmOpen, setApproverConfirmOpen] = useState(false);
const [approverDetails, setApproverDetails] = useState(null);
const [validPassDialogOpen, setValidPassDialogOpen] = useState(false);
const [validPassDetails, setValidPassDetails] = useState(null);

// Add these functions to handle the dialog
const openApproverConfirmDialog = (details) => {
  setApproverDetails(details);
  setApproverConfirmOpen(true);
};

const handleApproverConfirm = () => {
  setApproverConfirmOpen(false);
  // Proceed with the verified approver
};

const handleApproverReject = () => {
  setApproverConfirmOpen(false);
  // Reset verification state
  setIsVerified(false);
  setVerifiedFields({
    name: false,
    contact: false,
    email: false
  });
  // Show guidance
  handleToastOpen("info", "Please verify using email or contact number for more accurate results.");
};

  // Fetch country codes from API
  const fetchCountryCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/getCountryCodes`
      );

      if (response.data && response.data.data) {
        // Handle updated API response structure
        setCountryCodes(response.data.data);
      } else {
        // Handle original API response structure
        setCountryCodes(response.data);
      }
    } catch (error) {
      console.error("Error fetching country codes:", error);
      handleToastOpen(
        "error",
        "Could not load country codes. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountryCodes();
  }, []);

  useEffect(() => {
    // Enable the Verify button only if either Full Name, Contact, or Email is filled
    setIsVerifyEnabled(
      (formData.whomToVisitName || "").trim() !== "" ||
        (formData.whomToVisitContact || "").trim() !== "" ||
        (formData.whomToVisitEmail || "").trim() !== ""
    );
  }, [
    formData.whomToVisitName,
    formData.whomToVisitContact,
    formData.whomToVisitEmail,
  ]);

  // Create a debounced function to look up visitor details
  const lookupVisitorDetails = useCallback(
    debounce(async (contactNumber) => {
      if (!contactNumber || contactNumber.length < 10) return;
      
      try {
        setIsLookingUp(true);
        setLookupError(null);
        
        // Format the contact number with country code
        const fullPhoneNumber = contactNumber.startsWith('+') 
          ? contactNumber 
          : `${selectedCountryCode}${contactNumber}`;
        
        const response = await axios.get(
          `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/visitor-by-contact`,
          { params: { contact_number: fullPhoneNumber } }
        );
        
// In the lookupVisitorDetails function in VisitorFormVM.js
if (response.data && response.data.success && response.data.data) {
  const visitorData = response.data.data;
  console.log("Visitor data from API:", visitorData);
  console.log("Document type received:", visitorData.document_type);
  
  
  // Store full visitor data for reference
  setPreviousVisitData(visitorData);
  
  // Format dates correctly for the form
  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Convert to YYYY-MM-DD format for input type="date"
    return new Date(dateString).toISOString().split('T')[0];
  };
  
  // Update the form with the visitor's details
  setFormData(prevData => {
    const newDocumentType = visitorData.document_type || prevData.documentType || "";
    console.log("Setting document type to:", newDocumentType);
    
    return {
      ...prevData,
      name: visitorData.name || visitorData.full_name?.split(' ')[0] || "",
      surname: visitorData.surname || (visitorData.full_name?.split(' ').slice(1).join(' ')) || "",
      email: visitorData.email || "",
      // Include visit dates with proper formatting
      visitFrom: formatDate(visitorData.visit_from) || "",
      visitTill: formatDate(visitorData.visit_till) || "",
      // ADDED: Include time fields from previous visit data
      accessTimeFrom: visitorData.access_time_from || prevData.accessTimeFrom || "09:00",
      accessTimeTill: visitorData.access_time_till || prevData.accessTimeTill || "17:00",
      purpose: visitorData.purpose || prevData.purpose || "",
      whomToVisitName: visitorData.whom_to_visit || "",
      whomToVisitContact: visitorData.approver_contact || "",
      whomToVisitEmail: visitorData.approver_email || "",
      // Explicitly set document type from response
      documentType: newDocumentType
    };
  });

          
          // Store document URLs if available
          if (visitorData.photo_url) {
            setPhotoUrl(visitorData.photo_url);
            setPhotoPreview(visitorData.photo_url);
            console.log("Photo URL set:", visitorData.photo_url);
          }
          
          if (visitorData.document_url) {
            setDocumentUrl(visitorData.document_url);
            setDocumentPreview(visitorData.document_url);
            console.log("Document URL set:", visitorData.document_url);
          }
          
          // If "Person to Visit" information is loaded, mark it as auto-filled
          if (visitorData.whom_to_visit || visitorData.approver_contact || visitorData.approver_email) {
            setAutoFilledPerson(true);
          }
          
          // Show success message
          handleToastOpen('success', 'Previous visitor details loaded successfully.');

          // Show status of previous pass if applicable
          if (visitorData.hasOwnProperty('isPassValid')) {
            if (visitorData.isPassValid) {
              handleToastOpen('info', 'Visitor already has a valid pass.');
            } else if (visitorData.status === 'Approved') {
              handleToastOpen('warning', 'Visitor has an expired pass.');
            }
          }
        }
      } catch (error) {
        console.error("Error looking up visitor details:", error);
   
      } finally {
        setIsLookingUp(false);
      }
    }, 800), // 800ms debounce to avoid too many API calls while typing
    [selectedCountryCode, handleToastOpen]
  );

  // Modify the existing handleInputChange
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    
    // If contact number changes and lookup is enabled, trigger lookup
    if (name === 'contact' && value.length >= 10 && isContactLookupEnabled) {
      lookupVisitorDetails(value);
    }
  };
  
  // Function to toggle contact lookup
  const toggleContactLookup = () => {
    setIsContactLookupEnabled(prev => !prev);
    // Clear any existing lookup errors when toggling
    setLookupError(null);
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    const file = files[0];

    if (!file) return;

    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_PHOTO_SIZE_MB) {
      handleToastOpen(
        "error",
        `File size should not exceed ${MAX_PHOTO_SIZE_MB} MB`
      );
      return;
    }

    if (name === "photo" && file) {
      setFormData((prevData) => ({ ...prevData, [name]: file }));
      setPhotoPreview(URL.createObjectURL(file));
      // Clear the previous photo URL since we're uploading a new file
      setPhotoUrl(null);
    } else if (name === "document" && file) {
      setFormData((prevData) => ({ ...prevData, [name]: file }));
      setDocumentPreview(URL.createObjectURL(file));
      // Clear the previous document URL since we're uploading a new file
      setDocumentUrl(null);
    }
  };

  const validateForm = () => {
    const currentDate = new Date().toISOString().split("T")[0];

    // Validate 'Visit From' date
    if (formData.visitFrom < currentDate) {
      handleToastOpen("error", "'From date' must be today or later.");
      return false;
    }

    // Validate 'Visit Till' date
    if (formData.visitTill < formData.visitFrom) {
      handleToastOpen("error", "'Till date' must be after 'From date'.");
      return false;
    }

    // ADDED: Validate time range
    if (formData.accessTimeFrom >= formData.accessTimeTill) {
      handleToastOpen("error", "Access start time must be before end time.");
      return false;
    }

    // ADDED: Validate time boundaries (6:00 AM to 10:00 PM)
    const startTime = formData.accessTimeFrom.split(':');
    const endTime = formData.accessTimeTill.split(':');
    const startHour = parseInt(startTime[0]);
    const endHour = parseInt(endTime[0]);
    const startMinutes = parseInt(startTime[1]);
    const endMinutes = parseInt(endTime[1]);

    // Convert to minutes for easier comparison
    const startTotalMinutes = startHour * 60 + startMinutes;
    const endTotalMinutes = endHour * 60 + endMinutes;
    const minAllowedMinutes = 6 * 60; // 6:00 AM
    const maxAllowedMinutes = 22 * 60; // 10:00 PM

    if (startTotalMinutes < minAllowedMinutes || endTotalMinutes > maxAllowedMinutes) {
      handleToastOpen("error", "Access time must be between 6:00 AM and 10:00 PM.");
      return false;
    }

    // Validate photo upload - allow either new upload or previous URL
    if (!formData.photo && !photoUrl) {
      handleToastOpen("error", "Please upload a photo.");
      return false;
    }

    // Validate document upload - allow either new upload or previous URL
    if (!formData.document && !documentUrl) {
      handleToastOpen("error", "Please upload a document.");
      return false;
    }

    // Validate document type
    if (!formData.documentType) {
      handleToastOpen("error", "Please select a document type.");
      return false;
    }

    // Validate email format using regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      handleToastOpen("error", "Please enter a valid email address.");
      return false;
    }

    // Validate purpose of visit (not more than 100 words)
    const wordCount = formData.purpose.trim().split(/\s+/).length;
    if (wordCount > 100) {
      handleToastOpen("error", "Purpose of visit should not exceed 100 words.");
      return false;
    }

    // Validate if approver is verified
    if (!isVerified) {
      handleToastOpen("error", "Please verify the approver before submitting.");
      return false;
    }

    return true; // Return true if all validations pass
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsClicked(true);
    setLoading(true);
  
    // ADDED: Time validation at the beginning
    if (formData.accessTimeFrom >= formData.accessTimeTill) {
      handleToastOpen("error", "Access start time must be before end time.");
      setLoading(false);
      return;
    }

    if (!validateForm()) {
      setLoading(false);
      return;
    }
  
    if (!isFormComplete()) {
      setLoading(false);
      handleToastOpen(
        "error",
        "Please fill all the required fields and upload the necessary files."
      );
      return;
    }
  
    try {
      const form = new FormData();
      const fullPhoneNumber = `${selectedCountryCode}${formData.contact}`;
      const whomToVisitFullPhoneNumber = formData.whomToVisitContact.startsWith(
        "+"
      )
        ? formData.whomToVisitContact
        : `${selectedWhomToVisitCountryCode}${formData.whomToVisitContact}`;
  
      // Add all text fields
      form.append("fullName", `${formData.name} ${formData.surname}`);
      form.append("contactNumber", fullPhoneNumber);
      form.append("email", formData.email);
      form.append("visitFrom", formData.visitFrom);
      form.append("visitTill", formData.visitTill);
      // ADDED: Time fields
      form.append("accessTimeFrom", formData.accessTimeFrom);
      form.append("accessTimeTill", formData.accessTimeTill);
      form.append("purpose", formData.purpose);
      form.append("whomToVisitName", formData.whomToVisitName);
      form.append("whomToVisitContact", whomToVisitFullPhoneNumber);
      form.append("whomToVisitEmail", formData.whomToVisitEmail);
      form.append("documentType", formData.documentType);
      form.append(
        "fe_url",
        window.location.href.split("/visitor-mgmt")[0] + "/visitor-mgmt"
      );
  
      // Add previous document URLs if using those instead of new uploads
      if (photoUrl && !formData.photo) {
        form.append("previousPhotoUrl", photoUrl);
      }
      
      if (documentUrl && !formData.document) {
        form.append("previousDocumentUrl", documentUrl);
      }
  
      // Explicitly append files with their field names if uploading new files
      if (formData.photo) {
        form.append("photo", formData.photo);
      }
  
      if (formData.document) {
        form.append("document", formData.document);
      }
  
      // Log form data for debugging
      for (let [key, value] of form.entries()) {
        console.log(
          `${key}: ${value instanceof File ? `File: ${value.name}` : value}`
        );
      }
  
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/addVisitor`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
  
      console.log("Form submission response:", response.data);
  
      // Check if the visitor already has a valid pass
// Check if the visitor already has a valid pass that overlaps with requested dates
if (response.data.data && response.data.data.hasValidPass) {
  const validPass = response.data.data.validPassDetails;
  
  // Format dates for display
  const fromDate = new Date(validPass.visit_from).toLocaleDateString();
  const tillDate = new Date(validPass.visit_till).toLocaleDateString();
  const newFromDate = new Date(formData.visitFrom).toLocaleDateString();
  const newTillDate = new Date(formData.visitTill).toLocaleDateString();
  
  // Set the valid pass details for the dialog
  setValidPassDetails({
    ...validPass,
    requestedFrom: formData.visitFrom,
    requestedTill: formData.visitTill
  });
  
  // Open the dialog to show details
  setValidPassDialogOpen(true);
  
  // Show a brief toast notification
  handleToastOpen(
    "warning", 
    `You already have a valid visitor pass for the requested dates (${fromDate} to ${tillDate}).`,
    5000 // shorter duration since we're showing a dialog
  );
  
  setLoading(false);
  return;
}
  
      // Regular success path - reset form
      setFormData({
        name: "",
        surname: "",
        contact: "",
        email: "",
        visitFrom: "",
        visitTill: "",
        accessTimeFrom: "09:00",    // ADDED: Reset time fields
        accessTimeTill: "17:00",    // ADDED: Reset time fields
        purpose: "",
        whomToVisitName: "",
        whomToVisitContact: "",
        whomToVisitEmail: "",
        photo: null,
        document: null,
        documentType: "",
      });
      setPhotoPreview(null);
      setDocumentPreview(null);
      setPhotoUrl(null);
      setDocumentUrl(null);
      setPreviousVisitData(null);
      setIsVerified(false);
      setVerifiedFields({
        name: false,
        contact: false,
        email: false
      });
      setAutoFilledPerson(false);
      setLoading(false);
      
      // Show appropriate success message based on response
      handleToastOpen("success", response.data.message || "Registration successful!");
    } catch (error) {
      console.error("Error adding visitor:", error);
      console.error(
        "Error details:",
        error.response ? error.response.data : "No response data"
      );
      setLoading(false);
      
      // Improved error handling with more specific messages
      if (error.response) {
        if (error.response.status === 413) {
          handleToastOpen("error", "Files are too large. Please use smaller files (max 2MB each).");
        } else if (error.response.status === 400) {
          handleToastOpen("error", error.response.data.message || "Invalid data. Please check your form.");
        } else if (error.response.status === 404) {
          handleToastOpen("error", "The specified approver could not be found. Please verify and try again.");
        } else if (error.response.status === 409) {
          // For duplicate visitor entries
          handleToastOpen("warning", "A visitor with these email or contact already exists.");
        } else if (error.response.status === 500) {
          handleToastOpen("error", error.response.data.message || "Server error. Please try again later.");
        } else {
          handleToastOpen("error", error.response.data.message || "Error submitting the form. Please try again.");
        }
      } else if (error.request) {
        handleToastOpen("error", "Network error. Please check your connection and try again.");
      } else {
        handleToastOpen("error", "Error submitting the form. Please try again.");
      }
    }
  };

  const handleOpenPhotoPreview = () => setOpenPhotoPreview(true);
  const handleClosePhotoPreview = () => setOpenPhotoPreview(false);
  const handleOpenDocumentPreview = () => setOpenDocumentPreview(true);
  const handleCloseDocumentPreview = () => setOpenDocumentPreview(false);

  const handleVerify = async () => {
    try {
      setLoading(true);
      const { whomToVisitName, whomToVisitContact, whomToVisitEmail } = formData;
  
      // Track which fields are being used for verification
      const fieldsUsed = {
        name: !!whomToVisitName && whomToVisitName.trim() !== "",
        contact: !!whomToVisitContact && whomToVisitContact.trim() !== "",
        email: !!whomToVisitEmail && whomToVisitEmail.trim() !== ""
      };
  
      // Make sure we have at least one field to verify with
      if (!fieldsUsed.name && !fieldsUsed.contact && !fieldsUsed.email) {
        handleToastOpen("error", "Please provide at least one field to verify.");
        setLoading(false);
        return;
      }
  
      // If only name is provided, don't allow verification - require email or contact
      if (fieldsUsed.name && !fieldsUsed.contact && !fieldsUsed.email) {
        handleToastOpen(
          "error", 
          "Name alone is not sufficient. Please also provide email or contact number for unique identification."
        );
        setLoading(false);
        return;
      }
  
      // Validate email format if provided
      if (whomToVisitEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(whomToVisitEmail)) {
        handleToastOpen("error", "Please enter a valid email address for the person to visit.");
        setLoading(false);
        return;
      }
  
      // Rest of the verification logic continues as before...
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/verifyApprover`,
        {
          full_name: whomToVisitName || null,
          contact_details: whomToVisitContact
            ? whomToVisitContact.startsWith("+")
              ? whomToVisitContact
              : `${selectedWhomToVisitCountryCode}${whomToVisitContact}`
            : null,
          email_id: whomToVisitEmail || null,
        }
      );
  
      if (response.status === 200) {
        if (response.data.success) {
          // Create verified fields tracking
          const newVerifiedFields = {
            name: fieldsUsed.name && !!response.data.fullName,
            contact: fieldsUsed.contact && !!response.data.contact_details,
            email: fieldsUsed.email && !!response.data.email
          };
  
          // Update form with verified data
          setFormData((prevData) => ({
            ...prevData,
            whomToVisitName: response.data.fullName || prevData.whomToVisitName,
            whomToVisitEmail: response.data.email || prevData.whomToVisitEmail,
            whomToVisitContact: response.data.contact_details || prevData.whomToVisitContact,
          }));
          
          setIsVerified(true);
          setVerifiedFields(newVerifiedFields);
          setAutoFilledPerson(false);
          handleToastOpen("success", "Approver verified successfully");
        } else {
          setIsVerified(false);
          setVerifiedFields({ name: false, contact: false, email: false });
          handleToastOpen("error", "No matching approver found. Please check your information.");
        }
      }
    } catch (error) {
      setIsVerified(false);
      setVerifiedFields({ name: false, contact: false, email: false });
      
      if (error.response?.status === 404) {
        handleToastOpen("error", "No matching approver found. Please check your information.");
      } else {
        handleToastOpen("error", "Verification failed. Please try again.");
      }
      
      console.error("Verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const isFormComplete = () => {
    return (
      (formData.name || "").trim() !== "" &&
      (formData.surname || "").trim() !== "" &&
      (formData.contact || "").trim() !== "" &&
      (formData.email || "").trim() !== "" &&
      (formData.visitFrom || "").trim() !== "" &&
      (formData.visitTill || "").trim() !== "" &&
      (formData.accessTimeFrom || "").trim() !== "" &&  // ADDED: Time field validation
      (formData.accessTimeTill || "").trim() !== "" &&   // ADDED: Time field validation
      formData.accessTimeFrom < formData.accessTimeTill &&  // ADDED: Time range validation
      (formData.purpose || "").trim() !== "" &&
      isVerified &&
      (formData.photo || photoUrl) && // Allow previous photo URL
      (formData.document || documentUrl) && // Allow previous document URL
      (formData.documentType || "").trim() !== ""
    );
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split("T")[0];

  return {
    formData,
    isVerified,
    photoPreview,
    documentPreview,
    openPhotoPreview,
    openDocumentPreview,
    isVerifyEnabled,
    isClicked,
    loading,
    selectedCountryCode,
    selectedWhomToVisitCountryCode,
    countryCodes,
    today,
    handleInputChange,
    handleFileChange,
    handleSubmit,
    handleOpenPhotoPreview,
    handleClosePhotoPreview,
    handleOpenDocumentPreview,
    handleCloseDocumentPreview,
    handleVerify,
    isFormComplete,
    setSelectedCountryCode,
    setSelectedWhomToVisitCountryCode,
    // New properties for contact lookup
    isContactLookupEnabled,
    isLookingUp,
    lookupError,
    toggleContactLookup,
    photoUrl,
    documentUrl,
    previousVisitData,
    // New property for verified fields
    verifiedFields,
    // New property to track auto-filled Person to Visit section
    autoFilledPerson,
    approverConfirmOpen,
    approverDetails,
    handleApproverConfirm,
    handleApproverReject,
    setApproverConfirmOpen,
    validPassDialogOpen,
    setValidPassDialogOpen,
    validPassDetails
  };
};

export default VisitorFormVM;